import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useUnifiedService } from "../../utilities/contexts/UnifiedServiceContext";
import { ServiceStep } from "../../utilities/types/unified-services";
import { useAutoScrollToTop } from "../../utilities/hooks/useScrollToTop";
import { getServiceStepFlow } from "../../utilities/helpers/serviceStepMapper";
import { hasAcceptedAgreement, acceptAgreement } from "../../utilities/helpers/agreementHelper";
import { hasAcceptedPhotoPrivacy, acceptPhotoPrivacy } from "../../utilities/helpers/photoPrivacyHelper";
import UnifiedStepIndicator from "./UnifiedStepIndicator";
import UnifiedStepNavigation from "./UnifiedStepNavigation";
import AgreementModal from "../shared/AgreementModal";
import PhotoPrivacyModal from "../shared/PhotoPrivacyModal";

import UnifiedGuideStep from "./steps/UnifiedGuideStep";
import UnifiedUploadStep from "./steps/UnifiedUploadStep";
import UnifiedFaceBlurStep from "./steps/UnifiedFaceBlurStep";
import UnifiedHeightInputStep from "./steps/UnifiedHeightInputStep";
import UnifiedProcessingStep from "./steps/UnifiedProcessingStep";
import UnifiedResultsStep from "./steps/UnifiedResultsStep";

const UnifiedService: React.FC = () => {
  const { serviceInterface, serviceConfig } = useUnifiedService();
  const { currentStep } = serviceInterface;
  const location = useLocation();

  // Check if user is in guest mode (free trial)
  const isGuestMode = location.pathname.includes("/guest/");

  // Agreement state
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);

  // Photo Privacy state
  const [showPhotoPrivacy, setShowPhotoPrivacy] = useState(false);
  const [photoPrivacyAccepted, setPhotoPrivacyAccepted] = useState(false);

  // Check agreement status on mount (skip for guest users)
  useEffect(() => {
    if (isGuestMode) {
      // Guest users don't need to see Terms & Conditions
      setAgreementAccepted(true);
      return;
    }

    const accepted = hasAcceptedAgreement();
    setAgreementAccepted(accepted);
    if (!accepted) {
      setShowAgreement(true);
    }
  }, [isGuestMode]);

  // Check photo privacy before upload step
  useEffect(() => {
    if (currentStep === ServiceStep.UPLOAD && agreementAccepted) {
      const accepted = hasAcceptedPhotoPrivacy();
      setPhotoPrivacyAccepted(accepted);
      if (!accepted) {
        setShowPhotoPrivacy(true);
      }
    }
  }, [currentStep, agreementAccepted]);

  const handleAgreementAccept = () => {
    acceptAgreement();
    setAgreementAccepted(true);
    setShowAgreement(false);
  };

  const handlePhotoPrivacyAccept = () => {
    acceptPhotoPrivacy();
    setPhotoPrivacyAccepted(true);
    setShowPhotoPrivacy(false);
  };

  useAutoScrollToTop(currentStep);

  const stepFlow = getServiceStepFlow(serviceConfig);

  const renderCurrentStep = () => {
    if (!stepFlow.isValidStep(currentStep)) {
      return <UnifiedGuideStep />;
    }

    switch (currentStep) {
      case ServiceStep.GUIDE:
        return <UnifiedGuideStep />;
      case ServiceStep.UPLOAD:
        return <UnifiedUploadStep />;
      case ServiceStep.FACE_BLUR:
        if (serviceConfig.processing.requiresFaceBlur) {
          return <UnifiedFaceBlurStep />;
        }

        return <UnifiedProcessingStep />;
      case ServiceStep.HEIGHT_INPUT:
        if (serviceConfig.configuration?.requiresHeight) {
          return <UnifiedHeightInputStep />;
        }

        return <UnifiedProcessingStep />;
      case ServiceStep.PROCESSING:
        return <UnifiedProcessingStep />;
      case ServiceStep.RESULTS:
        return <UnifiedResultsStep />;
      default:
        return <UnifiedGuideStep />;
    }
  };

  return (
    <>
      {/* Agreement Modal - Shows before user can use the service (hidden for guests) */}
      {!isGuestMode && <AgreementModal visible={showAgreement && !agreementAccepted} onAccept={handleAgreementAccept} />}

      {/* Photo Privacy Modal - Shows before upload step */}
      <PhotoPrivacyModal visible={showPhotoPrivacy && !photoPrivacyAccepted && agreementAccepted} onAccept={handlePhotoPrivacyAccept} />

      <div className="min-h-dvh" style={{ backgroundColor: "#f5f5f5" }}>
        <div className="p-2 mx-auto md:p-4">
          <div className="w-full px-2 py-4 mx-auto bg-white lg:p-6 rounded-3xl">
            <UnifiedStepIndicator />
            <div className="min-h-fit">{renderCurrentStep()}</div>
            {currentStep !== ServiceStep.PROCESSING && currentStep !== ServiceStep.RESULTS && currentStep !== ServiceStep.FACE_BLUR && <UnifiedStepNavigation />}
          </div>
        </div>
      </div>
    </>
  );
};

export default UnifiedService;
